<script setup lang="ts">
import { shallowRef } from 'vue';
import MarketplaceTopOfBookWatchlistForm from '@/modules/marketplace/components/MarketplaceTopOfBookWatchlistForm.vue';
import MarketplaceTopOfBookWatchlistTable from '@/modules/marketplace/components/MarketplaceTopOfBookWatchlistTable.vue';
import { TopOfBookWatchlist } from '@/modules/marketplace/models';

const emit = defineEmits<{
  (event: 'close-modal' | 'refresh-watchlists'): void;
}>();

defineProps<{ watchlists: TopOfBookWatchlist[] }>();

const createFormStatus = shallowRef<'closed' | 'form' | 'upload'>('closed');
const editDisplayId = shallowRef<string | null>(null);

function closeDialog(): void {
  emit('close-modal');
}
</script>

<template>
  <span>
    <MarketplaceTopOfBookWatchlistForm
      v-if="createFormStatus !== 'closed' || editDisplayId"
      :display-id="editDisplayId"
      :initial-step="createFormStatus === 'upload' ? 'upload' : 'form'"
      @close-modal="
        editDisplayId = null;
        createFormStatus = 'closed';
      "
      @refresh-watchlists="emit('refresh-watchlists')"
    />

    <VDialog
      v-shortkey="['esc']"
      content-class="au-popup-dialog"
      max-width="600"
      overlay-color="secondary"
      overlay-opacity="0.80"
      persistent
      :value="true"
      @click:outside="closeDialog()"
      @keydown.esc="closeDialog()"
      @shortkey.native="closeDialog()"
    >
      <VForm novalidate @submit.prevent>
        <VCard class="d-flex flex-column">
          <VCardTitle>
            <span class="headline">Watchlists</span>
            <VBtn
              class="icon-create ml-4"
              color="primary"
              data-test-watchlist-manager="create"
              split
              @click="createFormStatus = 'form'"
            >
              Create New
            </VBtn>
            <VBtn
              class="icon-upload"
              color="primary"
              data-test-watchlist-manager="upload"
              @click="createFormStatus = 'upload'"
            >
              <VIcon size="24"> mdi-cloud-upload </VIcon>
            </VBtn>
            <VBtn class="close-icon" icon @click="closeDialog()">
              <VIcon>mdi-close</VIcon>
            </VBtn>
          </VCardTitle>

          <VCardText>
            <VContainer>
              <MarketplaceTopOfBookWatchlistTable
                :watchlists="watchlists"
                @edit="editDisplayId = $event"
                @remove="emit('refresh-watchlists')"
              />
            </VContainer>
          </VCardText>
        </VCard>
      </VForm>
    </VDialog>
  </span>
</template>

<style lang="scss" scoped>
.close-icon {
  top: 0.4rem;
  right: 0.4rem;
  position: absolute;
}

::v-deep {
  .v-data-table,
  .v-data-table > div {
    width: 100%;
    display: flex;
  }
}

.table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 50vh;
}

.table-container > div {
  display: flex;
  overflow: hidden;
}

.icon-action {
  opacity: 0.4;
  transition: opacity linear 0.15s;
}

.icon-action:hover {
  opacity: 1;
}

.icon-create.primary {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin: 0 1px 0 0;
}

.icon-upload.primary {
  width: 24px;
  min-width: 20px;
  padding-left: 24px;
  padding-right: 24px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
